// Effect recipes (§11): the look of every juice moment, in sim coordinates (x, y on the ground
// plane, z up). Each recipe spawns instanced particles, ground decals and text pops; counts scale
// with the quality tier (`density`) but never below what keeps the moment readable on Potato.
import { Color } from 'three';
import { COLORBLIND_IDENTITY_HEX, IDENTITIES } from '@shared/cosmetics';
import { Cell } from './atlas';
import { DecalAnim, type DecalSystem } from './Decals';
import { Curve, type ParticleSystem } from './Particles';

export const PALETTE = {
  ink: '#2A1A3A', cream: '#FFF6E9', white: '#FFFFFF', yellow: '#FFC93C', gold: '#FFD24A', pink: '#FF4F9A',
  danger: '#FF4D4D', positive: '#3DDC97', spark: '#FFE680', sky: '#7FD3FF', cardboard: '#D9A86C', banana: '#FFD93C',
  fireCore: '#FFE37A', fireMid: '#FF8A2E', fireEdge: '#FF4A1C', smoke: '#8C7CA0', smokeLight: '#D2C8DE', silver: '#CFD8E3',
} as const;

function col(hex: string): Color {
  return new Color(hex);
}

/** Linear-space colors, built once. */
export const C = {
  ink: col(PALETTE.ink), cream: col(PALETTE.cream), white: col(PALETTE.white), yellow: col(PALETTE.yellow),
  gold: col(PALETTE.gold), pink: col(PALETTE.pink), danger: col(PALETTE.danger), positive: col(PALETTE.positive),
  spark: col(PALETTE.spark), sky: col(PALETTE.sky), cardboard: col(PALETTE.cardboard), banana: col(PALETTE.banana),
  fireCore: col(PALETTE.fireCore), fireMid: col(PALETTE.fireMid), fireEdge: col(PALETTE.fireEdge), smoke: col(PALETTE.smoke),
  smokeLight: col(PALETTE.smokeLight), silver: col(PALETTE.silver),
};

const IDENTITY = IDENTITIES.map((i) => col(i.hex));
const IDENTITY_CB = COLORBLIND_IDENTITY_HEX.map(col);

export function identityColor(identity: number, colorblind: boolean): Color {
  const list = colorblind ? IDENTITY_CB : IDENTITY;
  return list[((identity % list.length) + list.length) % list.length];
}

export function identityHex(identity: number, colorblind: boolean): string {
  const i = ((identity % 8) + 8) % 8;
  return colorblind ? COLORBLIND_IDENTITY_HEX[i] : IDENTITIES[i].hex;
}

function rand(a: number, b: number): number {
  return a + Math.random() * (b - a);
}

function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

const CONFETTI_ACCENTS = [C.white, C.yellow, C.pink, C.sky, C.positive];

/** The context every recipe draws with. */
export class Fx {
  /** Particle-count multiplier for the tier (1 on High). */
  density = 1;
  /** Floor tint for dust (the map base blended toward cream). */
  readonly dust = new Color(PALETTE.cream);
  /** Splash droplet colors (void liquid) and whether the void is the electric pinball grid. */
  readonly liquid = new Color('#8A4B22');
  readonly foam = new Color('#F2D7B0');
  electricVoid = false;
  fogVoid = false;
  /** The arena floor is pale (speed lines draw in ink rather than white). */
  paleFloor = true;
  private readonly tmp = new Color();
  private readonly tmp2 = new Color();

  constructor(readonly particles: ParticleSystem, readonly decals: DecalSystem) {}

  private n(base: number, min = 1): number {
    return Math.max(min, Math.round(base * this.density));
  }

  /** Lunge / landing dust: soft cream puffs that billow and settle. */
  dustPuffs(x: number, y: number, z: number, count: number, dirX = 0, dirY = 0, speed = 1.5, size = 0.42): void {
    const p = this.particles;
    const s = p.spec;
    const n = this.n(count);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = speed * rand(0.4, 1);
      // Kicked out to the sides of the motion (straight behind a bean they hide under its body).
      const side = (i % 2 === 0 ? 1 : -1) * rand(0.25, 0.45);
      s.reset();
      s.x = x + rand(-0.12, 0.12) - dirY * side;
      s.y = z + 0.12;
      s.z = y + rand(-0.12, 0.12) + dirX * side;
      s.vx = Math.cos(a) * sp * 0.6 - dirX * speed;
      s.vz = Math.sin(a) * sp * 0.6 - dirY * speed;
      s.vy = rand(0.4, 1.4);
      s.drag = 4;
      s.gravity = -0.6;
      s.life = rand(0.4, 0.65);
      s.size = size * rand(0.7, 1);
      s.sizeEnd = s.size * 1.7;
      s.curve = Curve.GROW;
      s.fadeAt = 0.35;
      s.cell = Cell.PUFF;
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-2, 2);
      s.color(this.dust);
      p.emit(s);
    }
  }

  /** Land dust ring: puffs racing outward plus an expanding ground ring. */
  dustRing(x: number, y: number, h: number, radius: number, count = 8): void {
    const p = this.particles;
    const s = p.spec;
    const n = this.n(count, 4);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rand(-0.2, 0.2);
      s.reset();
      s.x = x + Math.cos(a) * radius * 0.4;
      s.y = h + 0.1;
      s.z = y + Math.sin(a) * radius * 0.4;
      const sp = radius * rand(3.2, 4.4);
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = rand(0.3, 0.9);
      s.drag = 6;
      s.life = rand(0.35, 0.55);
      s.size = 0.3 + radius * 0.2;
      s.sizeEnd = s.size * 1.6;
      s.curve = Curve.GROW;
      s.fadeAt = 0.3;
      s.cell = Cell.PUFF;
      s.rot = rand(0, Math.PI * 2);
      s.color(this.dust);
      p.emit(s);
    }
    const d = this.decals.spec.reset();
    d.x = x;
    d.y = y;
    d.h = h;
    d.width = d.length = radius * 0.8;
    d.scaleEnd = 2.4;
    d.life = 0.35;
    d.fadeAt = 0.2;
    d.anim = DecalAnim.EXPAND;
    d.cell = Cell.RING;
    d.color(this.dust);
    d.alpha = 0.85;
    this.decals.add(d);
  }

  /**
   * The shove impact (§11.1): a jagged comic burst at the contact that pops and vanishes within
   * 0.14 s, a hot core, action lines shooting out, 12 ink-outlined stars thrown along the knockback
   * and a few streaking sparks. `soft` (Reduced Flashing) dims the hot core.
   */
  impact(x: number, y: number, z: number, kbx: number, kby: number, strength: number, starCount = 12, soft = false): void {
    const p = this.particles;
    const s = p.spec;
    // Hot core (additive, short).
    s.reset();
    s.x = x;
    s.y = z;
    s.z = y;
    s.additive = true;
    s.cell = Cell.GLOW;
    s.size = 0.9 * strength;
    s.sizeEnd = 1.7 * strength;
    s.life = 0.1;
    s.fadeAt = 0;
    s.alpha = soft ? 0.3 : 0.85;
    s.color(C.spark);
    p.emit(s);
    // The impact frame: a big white jagged burst for a few frames behind everything (the SMACK).
    // Reduced Flashing drops it (a white frame).
    if (!soft) {
      s.reset();
      s.x = x;
      s.y = z;
      s.z = y;
      s.cell = Cell.IMPACT;
      s.size = 2.3 * strength;
      s.sizeEnd = 1.6 * strength;
      s.life = 0.075;
      s.fadeAt = 0.7;
      s.rot = rand(0, Math.PI * 2);
      s.color(C.cream);
      p.emit(s);
    }
    // The burst: pops to full size in a few frames, then shrinks away.
    s.reset();
    s.x = x;
    s.y = z;
    s.z = y;
    s.cell = Cell.BURST;
    s.size = 1.55 * strength;
    s.sizeEnd = 0.5 * strength;
    s.curve = Curve.POP;
    s.life = 0.14;
    s.fadeAt = 0.6;
    s.rot = rand(0, Math.PI * 2);
    s.color(C.yellow);
    p.emit(s);
    const kl = Math.hypot(kbx, kby) || 1;
    const ux = kbx / kl;
    const uy = kby / kl;
    // Action lines radiating from the contact.
    const lines = this.n(8, 5);
    for (let i = 0; i < lines; i++) {
      const a = (i / lines) * Math.PI * 2 + rand(-0.2, 0.2);
      const sp = rand(14, 22) * (0.8 + 0.2 * strength);
      s.reset();
      s.x = x + Math.cos(a) * 0.35;
      s.y = z + Math.sin(a) * 0.3;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vy = Math.sin(a) * sp * 0.8;
      s.vz = rand(-2, 2);
      s.drag = 9;
      s.life = rand(0.12, 0.17);
      s.size = 0.12;
      s.sizeEnd = 0.05;
      s.stretch = 0.045;
      s.fadeAt = 0.4;
      s.cell = Cell.STREAK;
      s.color(C.white);
      p.emit(s);
    }
    const n = this.n(starCount, 5);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(5, 10) * (0.8 + 0.2 * strength);
      s.reset();
      s.x = x;
      s.y = z;
      s.z = y;
      s.vx = Math.cos(a) * sp * 0.7 + ux * sp * 0.6;
      s.vz = Math.sin(a) * sp * 0.7 + uy * sp * 0.6;
      s.vy = rand(2, 7);
      s.gravity = 14;
      s.drag = 3.5;
      s.life = rand(0.4, 0.6);
      s.size = rand(0.24, 0.38) * (0.85 + 0.15 * strength);
      s.sizeEnd = 0.05;
      s.curve = Curve.HOLD;
      s.fadeAt = 0.8;
      s.cell = i % 3 === 2 ? Cell.SPARKLE : Cell.STAR;
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-12, 12);
      s.color(i % 2 === 0 ? C.yellow : C.white);
      p.emit(s);
    }
    const sparks = this.n(6, 2);
    for (let i = 0; i < sparks; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(9, 16);
      s.reset();
      s.x = x;
      s.y = z;
      s.z = y;
      s.vx = Math.cos(a) * sp * 0.6 + ux * sp * 0.7;
      s.vz = Math.sin(a) * sp * 0.6 + uy * sp * 0.7;
      s.vy = rand(0, 5);
      s.drag = 6;
      s.life = rand(0.15, 0.26);
      s.size = 0.07;
      s.sizeEnd = 0.02;
      s.stretch = 0.035;
      s.additive = true;
      s.cell = Cell.SPARK;
      s.color(C.spark);
      p.emit(s);
    }
  }

  /** A ground shockwave ring decal expanding to `radius` (m) in `life` seconds. */
  groundWave(x: number, y: number, h: number, radius: number, color: Color = C.white, life = 0.28, alpha = 0.9): void {
    const d = this.decals.spec.reset();
    d.x = x;
    d.y = y;
    d.h = h;
    d.width = d.length = 0.5;
    d.scaleEnd = (radius * 2) / 0.5;
    d.life = life;
    d.fadeAt = 0.35;
    d.anim = DecalAnim.EXPAND;
    d.cell = Cell.WAVE;
    d.color(color);
    d.alpha = alpha;
    this.decals.add(d);
  }

  /** Ground cracks (stomps, heavy landings), fading over `life` seconds. */
  cracks(x: number, y: number, h: number, size: number, life = 1.2): void {
    const d = this.decals.spec.reset();
    d.x = x;
    d.y = y;
    d.h = h;
    d.angle = rand(0, Math.PI * 2);
    d.width = d.length = size;
    d.life = life;
    d.fadeAt = 0.4;
    d.anim = DecalAnim.POP;
    d.scaleEnd = 1;
    d.cell = Cell.CRACK;
    d.color(C.ink);
    d.alpha = 0.7;
    this.decals.add(d);
  }

  /** CLASH (§11.1): a fountain of 30 yellow-white sparks at the midpoint. */
  sparkFountain(x: number, y: number, z: number, h: number, count = 30): void {
    const p = this.particles;
    const s = p.spec;
    const n = this.n(count, 10);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(2, 6);
      s.reset();
      s.x = x;
      s.y = z;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = rand(5, 11);
      s.gravity = 22;
      s.drag = 0.8;
      s.floor = h + 0.03;
      s.bounce = 0.3;
      s.life = rand(0.45, 0.8);
      s.size = 0.26;
      s.sizeEnd = 0.1;
      s.stretch = 0.07;
      s.fadeAt = 0.6;
      s.additive = i % 4 !== 0;
      s.cell = s.additive ? Cell.SPARK : Cell.SPARKLE;
      if (!s.additive) {
        s.size = 0.46;
        s.stretch = 0;
        s.spin = rand(-10, 10);
      }
      s.color(i % 3 === 0 ? C.white : C.spark);
      p.emit(s);
    }
    // A white-blue "kapow" burst at the midpoint and the floor wave.
    s.reset();
    s.x = x;
    s.y = z;
    s.z = y;
    s.cell = Cell.BURST2;
    s.size = 1.7;
    s.sizeEnd = 0.6;
    s.curve = Curve.POP;
    s.life = 0.16;
    s.fadeAt = 0.55;
    s.rot = rand(0, Math.PI);
    s.color(C.sky);
    p.emit(s);
    this.groundWave(x, y, h, 1.8, C.white, 0.24, 0.85);
  }

  /** Twinkling sparkles (CLUTCH!, crate open, pickups). */
  sparkles(x: number, y: number, z: number, count: number, color: Color, spread = 1.2, rise = 2): void {
    const p = this.particles;
    const s = p.spec;
    const n = this.n(count, 4);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = rand(0.2, spread);
      s.reset();
      s.x = x + Math.cos(a) * r * 0.3;
      s.y = z + rand(0, 0.6);
      s.z = y + Math.sin(a) * r * 0.3;
      s.vx = Math.cos(a) * r * 2.4;
      s.vz = Math.sin(a) * r * 2.4;
      s.vy = rand(0.5, rise * 1.6);
      s.drag = 3;
      s.gravity = 1.5;
      s.life = rand(0.55, 0.9);
      s.size = rand(0.22, 0.36);
      s.sizeEnd = 0;
      s.curve = Curve.POP;
      s.fadeAt = 0.7;
      s.cell = i % 4 === 0 ? Cell.STAR : Cell.SPARKLE;
      s.spin = rand(-6, 6);
      s.color(i % 3 === 0 ? C.white : color);
      p.emit(s);
    }
  }

  /** Confetti (KO celebration, round win): flat paper pieces that flutter down. */
  confetti(x: number, y: number, z: number, count: number, main: Color, upSpeed = 9, spread = 4, floor = 0): void {
    const p = this.particles;
    const s = p.spec;
    const n = this.n(count, 30);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(0.5, 1) * spread;
      s.reset();
      s.x = x;
      s.y = z + 0.3;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = upSpeed * rand(0.6, 1.15);
      s.gravity = 7;
      s.drag = 1.8;
      s.floor = floor + 0.02;
      s.bounce = 0.1;
      s.life = rand(1.8, 2.8);
      s.size = rand(0.2, 0.3);
      s.sizeEnd = s.size;
      s.aspect = rand(0.5, 0.8);
      s.fadeAt = 0.75;
      s.cell = i % 7 === 0 ? Cell.STAR : Cell.CONFETTI;
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-9, 9);
      s.flutter = rand(1.5, 3.5);
      s.color(i % 3 === 0 ? pick(CONFETTI_ACCENTS) : main);
      p.emit(s);
    }
  }

  /**
   * KO splash (§11.2): a crown of 24 droplets thrown up and out from the void surface at `h`, a tall
   * central column, a fine spray and a bright flash, all in the void's liquid and foam colors. The
   * electric Pinball grid zaps instead; cloud voids (Lobby lilac, Spin foam) burst in puffs.
   */
  splash(x: number, y: number, h: number, count = 24): void {
    const p = this.particles;
    const s = p.spec;
    const n = this.n(count, 14);
    // Flash on the surface.
    s.reset();
    s.x = x;
    s.y = h + 0.4;
    s.z = y;
    s.additive = true;
    s.cell = Cell.GLOW;
    s.size = 2.2;
    s.sizeEnd = 4.2;
    s.life = 0.18;
    s.fadeAt = 0;
    s.alpha = 0.5;
    s.color(this.foam);
    p.emit(s);
    if (this.electricVoid) {
      for (let i = 0; i < n * 2; i++) {
        const a = (i / (n * 2)) * Math.PI * 2;
        const sp = rand(4, 9);
        s.reset();
        s.x = x;
        s.y = h + 0.1;
        s.z = y;
        s.vx = Math.cos(a) * sp;
        s.vz = Math.sin(a) * sp;
        s.vy = rand(5, 13);
        s.gravity = 18;
        s.life = rand(0.4, 0.7);
        s.size = 0.12;
        s.sizeEnd = 0.04;
        s.stretch = 0.06;
        s.additive = true;
        s.cell = Cell.SPARK;
        s.color(i % 3 === 0 ? C.white : i % 2 ? this.liquid : this.foam);
        p.emit(s);
      }
      this.airRing(x, y, h + 0.2, 4.5, this.foam, 0.35);
      this.airRing(x, y, h + 0.2, 3, this.liquid, 0.25);
      return;
    }
    const cloud = this.fogVoid;
    const cell = cloud ? Cell.PUFF : Cell.BLOB;
    // Crown droplets are foam and a lighter tea (the dark liquid alone reads as chocolate chips).
    const drop = this.tmp2.copy(this.liquid).lerp(this.foam, 0.35);
    // A ring of foam puffs boiling up at the impact (the base of the crown).
    const base = this.n(9, 6);
    for (let i = 0; i < base; i++) {
      const a = (i / base) * Math.PI * 2 + rand(-0.2, 0.2);
      s.reset();
      s.x = x + Math.cos(a) * 0.7;
      s.y = h + 0.15;
      s.z = y + Math.sin(a) * 0.7;
      s.vx = Math.cos(a) * 3.2;
      s.vz = Math.sin(a) * 3.2;
      s.vy = rand(1.5, 3);
      s.drag = 3;
      s.gravity = 4;
      s.life = rand(0.6, 0.85);
      s.size = rand(0.85, 1.15);
      s.sizeEnd = s.size * 1.35;
      s.curve = Curve.GROW;
      s.fadeAt = 0.45;
      s.rot = rand(0, Math.PI * 2);
      s.cell = Cell.PUFF;
      s.color(this.foam);
      p.emit(s);
    }
    // The crown: big ink-rimmed droplets thrown up and out.
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rand(-0.12, 0.12);
      const out = rand(2.4, 4.6);
      s.reset();
      s.x = x + Math.cos(a) * 0.6;
      s.y = h + 0.1;
      s.z = y + Math.sin(a) * 0.6;
      s.vx = Math.cos(a) * out;
      s.vz = Math.sin(a) * out;
      s.vy = rand(8, 13);
      s.gravity = 24;
      s.floor = h;
      s.bounce = -1;
      s.life = rand(0.8, 1.05);
      s.size = cloud ? rand(0.7, 1) : rand(0.36, 0.56);
      s.sizeEnd = s.size * 0.6;
      s.stretch = cloud ? 0 : 0.012;
      s.fadeAt = 0.8;
      s.rot = rand(0, Math.PI * 2);
      s.cell = cell;
      s.color(i % 3 === 0 ? drop : this.foam);
      p.emit(s);
    }
    // The crown sheet: a cartoon splash cup flaring up from the surface for half a second.
    if (!cloud) {
      s.reset();
      s.x = x;
      s.y = h + 1.1;
      s.z = y;
      s.vy = 1.2;
      s.drag = 3;
      s.life = 0.5;
      s.size = 3.2;
      s.sizeEnd = 2.4;
      s.curve = Curve.POP;
      s.fadeAt = 0.55;
      s.cell = Cell.SPLASH;
      s.color(this.foam);
      p.emit(s);
    }
    // The central column: a tall stack of blobs that shoots up (the top first) and falls back.
    const col = this.n(8, 4);
    for (let i = 0; i < col; i++) {
      s.reset();
      s.x = x + rand(-0.12, 0.12);
      s.y = h + 0.1 + i * 0.25;
      s.z = y + rand(-0.12, 0.12);
      s.vy = 9 + i * 1.5;
      s.gravity = 22;
      s.floor = h;
      s.bounce = -1;
      s.life = 0.95 + i * 0.05;
      s.size = cloud ? 1.2 - i * 0.08 : 0.95 - i * 0.07;
      s.sizeEnd = s.size * 0.55;
      s.stretch = cloud ? 0 : 0.008;
      s.fadeAt = 0.78;
      s.cell = cell;
      s.color(i % 3 === 1 ? drop : this.foam);
      p.emit(s);
    }
    // Fine spray.
    for (let i = 0; i < this.n(18, 6); i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(3, 8);
      s.reset();
      s.x = x;
      s.y = h + 0.2;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = rand(5, 11);
      s.gravity = 20;
      s.floor = h;
      s.bounce = -1;
      s.drag = 1;
      s.life = rand(0.5, 0.8);
      s.size = rand(0.14, 0.22);
      s.sizeEnd = 0.06;
      s.fadeAt = 0.6;
      s.cell = Cell.DROP;
      s.color(i % 2 ? this.liquid : this.foam);
      p.emit(s);
    }
    // A foam patch left on a liquid surface (fog and suds voids have no surface to mark).
    if (cloud) return;
    const d = this.decals.spec.reset();
    d.x = x;
    d.y = y;
    d.h = h + 0.02;
    d.angle = rand(0, Math.PI * 2);
    d.width = d.length = 2.2;
    d.scaleEnd = 1.6;
    d.life = 1.4;
    d.fadeAt = 0.4;
    d.anim = DecalAnim.POP;
    d.cell = Cell.SPLAT;
    d.color(this.foam);
    d.alpha = 0.9;
    this.decals.add(d);
  }

  /**
   * Bomb / potato explosion (§11.3), without the camera and post parts. A layered toon fireball that
   * cools as it dies: a white-hot flash, then a dome of ink-rimmed fire puffs drawn red under orange
   * under yellow, the yellow core shrinking first so the ball reads hot → cool; flame tongues licking
   * up; rising smoke clouds that take over as the fire burns out; 40 debris chunks and sparks; the
   * 0 → 2.5 m ground shockwave in 0.25 s and a scorch that fades in 6 s.
   */
  explosion(x: number, y: number, z: number, h: number, radius: number, debrisColor: Color, soft = false): void {
    const p = this.particles;
    const s = p.spec;
    const R = radius;
    // Flash (Reduced Flashing: dimmer, smaller).
    s.reset();
    s.x = x;
    s.y = z + 0.8;
    s.z = y;
    s.additive = true;
    s.cell = Cell.GLOW;
    s.size = R * (soft ? 1.1 : 1.6);
    s.sizeEnd = R * 2.3;
    s.life = 0.09;
    s.fadeAt = 0;
    s.alpha = soft ? 0.35 : 0.8;
    s.color(C.fireCore);
    p.emit(s);
    // Smoke clouds, drawn first so the fire covers them; they appear as the fireball cools and
    // billow up and out.
    const clouds = this.n(9, 5);
    for (let i = 0; i < clouds; i++) {
      const a = (i / clouds) * Math.PI * 2 + rand(-0.3, 0.3);
      const r = R * rand(0.15, 0.4);
      s.reset();
      s.x = x + Math.cos(a) * r;
      s.y = z + R * rand(0.25, 0.75);
      s.z = y + Math.sin(a) * r;
      s.vx = Math.cos(a) * rand(1, 2.2);
      s.vz = Math.sin(a) * rand(1, 2.2);
      s.vy = rand(2, 3.4);
      s.drag = 1.4;
      s.life = rand(1.1, 1.6);
      s.size = R * rand(0.36, 0.48);
      s.sizeEnd = s.size * 1.6;
      s.curve = Curve.GROW;
      s.fadeAt = 0.4;
      s.cell = Cell.CLOUD;
      s.delay = rand(0.3, 0.42);
      s.rot = rand(-0.3, 0.3);
      s.color(i % 3 === 0 ? C.smoke : C.smokeLight);
      p.emit(s);
    }
    // The fireball: three shells of puffs on a dome, outer red (lives longest), then orange, then
    // the yellow core on top (dies first). Each puff swells in a few frames and drifts outward.
    const shells: readonly [Color, number, number, number, number, Cell][] = [
      // color, count, dome radius (×R), puff size (×R), life (s), sprite
      [C.fireEdge, 6, 0.34, 0.72, 0.62, Cell.EMBER],
      [C.fireMid, 5, 0.24, 0.64, 0.48, Cell.FIRE],
      [C.fireCore, 3, 0.1, 0.7, 0.32, Cell.FIRE],
    ];
    for (const [c, count, dome, size, life, cell] of shells) {
      const n = this.n(count, Math.ceil(count * 0.6));
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + rand(-0.4, 0.4);
        const el = rand(0.15, 1.1);
        const ox = Math.cos(a) * Math.cos(el);
        const oz = Math.sin(a) * Math.cos(el);
        const oy = Math.sin(el);
        s.reset();
        s.x = x + ox * dome * R;
        s.y = z + 0.35 * R + oy * dome * R;
        s.z = y + oz * dome * R;
        s.vx = ox * R * 1.6;
        s.vz = oz * R * 1.6;
        s.vy = oy * R * 1.2 + 1.2;
        s.drag = 5;
        s.life = life * rand(0.85, 1.15);
        s.size = size * R * rand(0.85, 1.15);
        s.sizeEnd = s.size * 0.35;
        s.curve = Curve.POP;
        s.fadeAt = 0.7;
        s.rot = rand(0, Math.PI * 2);
        s.spin = rand(-1.5, 1.5);
        s.cell = cell;
        s.color(c);
        p.emit(s);
      }
    }
    // Flame tongues licking up from the top of the ball.
    for (let i = 0; i < this.n(5, 3); i++) {
      const a = Math.random() * Math.PI * 2;
      s.reset();
      s.x = x + Math.cos(a) * R * 0.25;
      s.y = z + R * 0.7;
      s.z = y + Math.sin(a) * R * 0.25;
      s.vx = Math.cos(a) * 1.2;
      s.vz = Math.sin(a) * 1.2;
      s.vy = rand(4, 6);
      s.drag = 4;
      s.life = rand(0.26, 0.36);
      s.size = R * rand(0.3, 0.4);
      s.sizeEnd = s.size * 0.3;
      s.aspect = 1.3;
      s.curve = Curve.POP;
      s.fadeAt = 0.55;
      s.rot = rand(-0.35, 0.35);
      s.cell = Cell.FLAME;
      s.color(i % 2 === 0 ? C.fireCore : C.fireMid);
      p.emit(s);
    }
    // The burst shape on top: a split-second comic "KA-" frame.
    s.reset();
    s.x = x;
    s.y = z + 0.35 * R;
    s.z = y;
    s.cell = Cell.BURST;
    s.size = R * 1.5;
    s.sizeEnd = R * 0.7;
    s.curve = Curve.POP;
    s.life = 0.1;
    s.fadeAt = 0.5;
    s.rot = rand(0, Math.PI * 2);
    s.color(C.fireCore);
    p.emit(s);
    // An air ring racing out at chest height.
    this.airRing(x, y, z + 0.5, R * 2.6, C.cream, 0.24);
    // 40 debris chunks.
    const debris = this.n(40, 12);
    for (let i = 0; i < debris; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(4, 11);
      s.reset();
      s.x = x;
      s.y = z + 0.4;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = rand(4, 11);
      s.gravity = 22;
      s.drag = 0.6;
      s.floor = h + 0.03;
      s.bounce = 0.35;
      s.life = rand(0.8, 1.3);
      s.size = rand(0.16, 0.28);
      s.sizeEnd = s.size * 0.8;
      s.fadeAt = 0.7;
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-14, 14);
      s.cell = i % 5 === 0 ? Cell.STAR : Cell.CHUNK;
      s.color(i % 5 === 0 ? C.yellow : i % 2 === 0 ? debrisColor : C.smoke);
      p.emit(s);
    }
    // Sparks.
    for (let i = 0; i < this.n(14, 5); i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(9, 17);
      s.reset();
      s.x = x;
      s.y = z + 0.5;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = rand(2, 9);
      s.gravity = 12;
      s.drag = 2;
      s.life = rand(0.25, 0.45);
      s.size = 0.09;
      s.sizeEnd = 0.03;
      s.stretch = 0.04;
      s.additive = true;
      s.cell = Cell.SPARK;
      s.color(C.fireCore);
      p.emit(s);
    }
    // Ground: the 0 → 2.5 m shockwave ring in 0.25 s and a scorch that fades over 6 s.
    this.groundWave(x, y, h, radius, C.cream, 0.25, 1);
    const d = this.decals.spec.reset();
    d.x = x;
    d.y = y;
    d.h = h;
    d.width = d.length = radius * 1.1;
    d.angle = rand(0, Math.PI * 2);
    d.life = 6;
    d.fadeAt = 0.6;
    d.cell = Cell.SCORCH;
    d.color(C.ink);
    d.alpha = 0.55;
    this.decals.add(d);
  }

  /** A shockwave ring billboard (air ring) — big hits and explosions. */
  airRing(x: number, y: number, z: number, size: number, color: Color = C.white, life = 0.22): void {
    const s = this.particles.spec.reset();
    s.x = x;
    s.y = z;
    s.z = y;
    s.cell = Cell.RING;
    s.size = size * 0.2;
    s.sizeEnd = size;
    s.life = life;
    s.fadeAt = 0.3;
    s.color(color);
    this.particles.emit(s);
  }

  /** Crate open (§11.3): 8 cardboard flaps and a sparkle. */
  crateBurst(x: number, y: number, z: number, h: number, soft = false): void {
    const p = this.particles;
    const s = p.spec;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + rand(-0.2, 0.2);
      const sp = rand(2.5, 5);
      s.reset();
      s.x = x;
      s.y = z + 0.5;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = rand(4, 7);
      s.gravity = 18;
      s.drag = 0.8;
      s.floor = h + 0.03;
      s.bounce = 0.25;
      s.life = rand(0.9, 1.3);
      s.size = rand(0.32, 0.45);
      s.sizeEnd = s.size * 0.9;
      s.fadeAt = 0.7;
      s.cell = Cell.FLAP;
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-12, 12);
      s.flutter = rand(2, 4);
      s.color(C.cardboard);
      p.emit(s);
    }
    this.sparkles(x, y, z + 0.6, 10, C.gold, 1.4, 2.5);
    this.impact(x, y, z + 0.6, 0, 0, 0.8, 0, soft);
  }

  /** Rubber shreds (balloon pop). */
  shreds(x: number, y: number, z: number, color: Color, count = 10): void {
    const p = this.particles;
    const s = p.spec;
    for (let i = 0; i < this.n(count, 5); i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = rand(3, 7);
      s.reset();
      s.x = x;
      s.y = z;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = rand(2, 6);
      s.gravity = 14;
      s.drag = 2;
      s.life = rand(0.6, 0.9);
      s.size = rand(0.22, 0.32);
      s.sizeEnd = s.size;
      s.fadeAt = 0.6;
      s.cell = Cell.SHRED;
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-15, 15);
      s.color(color);
      p.emit(s);
    }
    this.airRing(x, y, z, 1.6, C.white, 0.18);
  }

  /** The BOUNTY crown flying off the victim (§11.2). */
  crownFly(x: number, y: number, z: number): void {
    const s = this.particles.spec.reset();
    s.x = x;
    s.y = z + 1.2;
    s.z = y;
    s.vx = rand(-1.5, 1.5);
    s.vz = rand(-1, 1);
    s.vy = 9;
    s.gravity = 14;
    s.life = 1.3;
    s.size = 0.7;
    s.sizeEnd = 0.7;
    s.fadeAt = 0.75;
    s.spin = rand(8, 12) * (Math.random() < 0.5 ? -1 : 1);
    s.cell = Cell.CROWN;
    s.color(C.gold);
    this.particles.emit(s);
    this.sparkles(x, y, z + 1.2, 10, C.gold, 1.2, 3);
  }

  /** Fireworks: rockets that burst into identity-colored star rings. */
  fireworks(x: number, y: number, z: number, main: Color): void {
    const p = this.particles;
    const s = p.spec;
    for (let k = 0; k < 3; k++) {
      const bx = x + rand(-1.5, 1.5);
      const by = y + rand(-1.5, 1.5);
      const bz = z + rand(5.5, 7.5);
      const n = this.n(18, 8);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const sp = rand(4, 6);
        s.reset();
        s.x = bx;
        s.y = bz;
        s.z = by;
        s.vx = Math.cos(a) * sp;
        s.vy = Math.sin(a) * sp;
        s.vz = rand(-1, 1);
        s.gravity = 3;
        s.drag = 2.2;
        s.life = rand(0.8, 1.2) + k * 0.1;
        s.size = 0.26;
        s.sizeEnd = 0.05;
        s.curve = Curve.HOLD;
        s.fadeAt = 0.6;
        s.cell = i % 2 ? Cell.SPARKLE : Cell.STAR;
        s.spin = rand(-6, 6);
        s.color(k === 1 ? C.white : main);
        p.emit(s);
      }
      // The rocket trail.
      s.reset();
      s.x = bx;
      s.y = z;
      s.z = by;
      s.vy = 14;
      s.life = 0.28;
      s.size = 0.12;
      s.stretch = 0.05;
      s.additive = true;
      s.cell = Cell.SPARK;
      s.color(C.spark);
      p.emit(s);
    }
  }

  /** Flower burst: petals that spiral out and drift down. */
  flowerBurst(x: number, y: number, z: number, main: Color): void {
    const p = this.particles;
    const s = p.spec;
    const n = this.n(36, 14);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 6;
      const sp = rand(2, 5);
      s.reset();
      s.x = x;
      s.y = z + 0.5;
      s.z = y;
      s.vx = Math.cos(a) * sp;
      s.vz = Math.sin(a) * sp;
      s.vy = rand(8, 12);
      s.gravity = 5;
      s.drag = 1.5;
      s.life = rand(1.4, 2.2);
      s.size = rand(0.28, 0.42);
      s.sizeEnd = s.size;
      s.fadeAt = 0.75;
      s.cell = i % 4 === 0 ? Cell.HEART : Cell.FLOWER;
      s.spin = rand(-4, 4);
      s.flutter = 1.2;
      s.color(i % 3 === 0 ? C.white : i % 3 === 1 ? main : C.pink);
      p.emit(s);
    }
  }

  /** Disco ball: a spinning ring of mirror tiles with glints. */
  disco(x: number, y: number, z: number, main: Color): void {
    const p = this.particles;
    const s = p.spec;
    const n = this.n(28, 12);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      s.reset();
      s.x = x + Math.cos(a) * 0.6;
      s.y = z + 4.6 + rand(-0.4, 0.4);
      s.z = y + Math.sin(a) * 0.6;
      s.vx = Math.cos(a) * 3.5;
      s.vz = Math.sin(a) * 3.5;
      s.vy = rand(-0.5, 1.5);
      s.drag = 1.2;
      s.gravity = 1;
      s.life = rand(1.2, 1.8);
      s.size = 0.24;
      s.sizeEnd = 0.24;
      s.fadeAt = 0.7;
      s.cell = i % 3 === 0 ? Cell.GLINT : Cell.DISCO;
      s.additive = i % 3 === 0;
      s.spin = 6;
      s.flutter = 3;
      s.color(i % 2 === 0 ? C.silver : main);
      p.emit(s);
    }
  }

  /** Speed lines streaking behind a fast bean (§11.1 tumble/throw). */
  speedLine(x: number, y: number, z: number, vx: number, vy: number, vz: number): void {
    const s = this.particles.spec.reset();
    const a = Math.random() * Math.PI * 2;
    s.x = x + Math.cos(a) * 0.55;
    s.y = z + 0.55 + Math.sin(a) * 0.45;
    s.z = y + Math.sin(a) * 0.35;
    s.vx = vx * 0.25;
    s.vy = vz * 0.25;
    s.vz = vy * 0.25;
    s.life = 0.18;
    s.size = 0.1;
    s.sizeEnd = 0.03;
    s.stretch = 0.34;
    s.fadeAt = 0.2;
    s.alpha = this.paleFloor ? 0.85 : 0.95;
    s.cell = Cell.SPEEDLINE;
    // Manga rule: dark lines over pale floors, white ones over dark floors.
    s.color(this.paleFloor ? C.ink : C.white);
    this.particles.emit(s);
  }

  /** One trail particle of a §7.3 trail cosmetic (index into the trail catalog). */
  trail(kind: number, x: number, y: number, z: number, main: Color, t: number): void {
    const s = this.particles.spec.reset();
    s.x = x + rand(-0.2, 0.2);
    s.y = z + rand(0.3, 0.8);
    s.z = y + rand(-0.2, 0.2);
    s.vy = rand(0.2, 0.8);
    s.drag = 2;
    s.life = rand(0.45, 0.7);
    s.size = rand(0.18, 0.28);
    s.sizeEnd = 0.05;
    s.fadeAt = 0.5;
    s.spin = rand(-4, 4);
    switch (kind) {
      case 1: s.cell = Cell.SPARKLE; s.color(Math.random() < 0.5 ? C.white : C.gold); break;
      case 2: s.cell = Cell.BUBBLE; s.color(C.sky); s.vy = 1; break;
      case 3: s.cell = Cell.HEART; s.color(C.pink); break;
      case 4: s.cell = Cell.NOTE; s.color(main); s.vy = 1.2; break;
      case 5: {
        s.cell = Cell.DOT;
        this.tmp.setHSL((t * 0.9) % 1, 0.95, 0.6);
        s.color(this.tmp);
        s.size = 0.45;
        s.sizeEnd = 0.3;
        s.fadeAt = 0.3;
        s.spin = 0;
        break;
      }
      default: return;
    }
    this.particles.emit(s);
  }

  /** A whiff swish in front of a bean (grab miss, punch). */
  swish(x: number, y: number, z: number, facing: number, color: Color = C.white, size = 1.1): void {
    const s = this.particles.spec.reset();
    s.x = x + Math.cos(facing) * 0.9;
    s.y = z + 0.6;
    s.z = y + Math.sin(facing) * 0.9;
    s.vx = Math.cos(facing) * 2;
    s.vz = Math.sin(facing) * 2;
    s.drag = 6;
    s.life = 0.22;
    s.size = size;
    s.sizeEnd = size * 1.2;
    s.fadeAt = 0.3;
    s.cell = Cell.SWISH;
    s.rot = rand(-0.3, 0.3);
    s.color(color);
    this.particles.emit(s);
  }

  /** Sweat drops flicking off a flushed bean (Dizzy ≥ 70). */
  sweat(x: number, y: number, headZ: number): void {
    const s = this.particles.spec.reset();
    const side = Math.random() < 0.5 ? -1 : 1;
    s.x = x + side * 0.3;
    s.y = headZ;
    s.z = y + rand(-0.1, 0.2);
    s.vx = side * rand(1, 2);
    s.vy = rand(1.5, 3);
    s.gravity = 14;
    s.life = 0.45;
    s.size = 0.16;
    s.sizeEnd = 0.1;
    s.fadeAt = 0.6;
    s.rot = -side * 0.6;
    s.cell = Cell.DROP;
    s.color(C.sky);
    this.particles.emit(s);
  }

  /** A skid streak decal under a slipping bean (fades over 4 s, §11.3). */
  skid(x: number, y: number, h: number, angle: number): void {
    const d = this.decals.spec.reset();
    d.x = x;
    d.y = y;
    d.h = h;
    d.angle = angle;
    d.width = 0.55;
    d.length = 0.55;
    d.life = 4;
    d.fadeAt = 0.5;
    d.cell = Cell.SKID;
    d.color(C.ink);
    d.alpha = 0.45;
    this.decals.add(d);
  }

  /** A paint-splat decal (peel placed / slipped). */
  splat(x: number, y: number, h: number, color: Color, size = 0.9, life = 2.5): void {
    const d = this.decals.spec.reset();
    d.x = x;
    d.y = y;
    d.h = h;
    d.angle = rand(0, Math.PI * 2);
    d.width = d.length = size;
    d.scaleEnd = 1;
    d.life = life;
    d.fadeAt = 0.6;
    d.anim = DecalAnim.POP;
    d.cell = Cell.SPLAT;
    d.color(color);
    d.alpha = 0.9;
    this.decals.add(d);
  }

  /** Mixes `a` toward `b`; returns a scratch color valid until the next call. */
  mix(a: Color, b: Color, t: number): Color {
    return this.tmp.copy(a).lerp(b, t);
  }
}
